export const BASE_COLORS = {
  blue: '#0F23DF',
  lightBlue: '#03B4DC',
  teal: '#20B2AA',
  green: '#3bc14a',
  darkGreen: '#2e7d32',
  yellow: '#E8C547',
  orange: '#FF9800',
  amber: '#FFC107',
  red: '#D62839',
  burgundy: '#8B1538',
  pink: '#E91E63',
  purple: '#9C27B0',
  indigo: '#3F51B5',
  brown: '#8D6E63',
  gray: '#6B7280',
  softGray: '#D7D7D7',
  white: '#FFFFFF',
};

export const CHART_COLORS = {
  blue: 'rgba(15, 35, 223, 0.7)',
  orange: 'rgba(255, 152, 0, 0.7)',
  green: 'rgba(59, 193, 74, 0.7)',
  teal: 'rgba(32, 178, 170, 0.7)',
  lightBlue: 'rgba(3, 180, 220, 0.7)',
  red: 'rgba(214, 40, 57, 0.7)',
  gray: 'rgba(107, 114, 128, 0.7)',
  purple: 'rgba(156, 39, 176, 0.7)',
  yellow: 'rgba(232, 197, 71, 0.7)',
  amber: 'rgba(255, 193, 7, 0.7)',
  darkGreen: 'rgba(46, 125, 50, 0.7)',
  burgundy: 'rgba(139, 21, 56, 0.7)',
  pink: 'rgba(233, 30, 99, 0.7)',
  indigo: 'rgba(63, 81, 181, 0.7)',
  brown: 'rgba(141, 110, 99, 0.7)',
  softGray: 'rgba(215, 215, 215, 0.7)',
};

function parseRgba(rgba: string): {
  r: number;
  g: number;
  b: number;
  a: number;
} {
  const match = rgba.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([\d.]+))?\)/);
  if (!match) throw new Error(`Invalid color: ${rgba}`);
  return {
    r: Number(match[1]) / 255,
    g: Number(match[2]) / 255,
    b: Number(match[3]) / 255,
    a: match[4] != null ? Number(match[4]) : 1,
  };
}

function rgbToHsl(
  r: number,
  g: number,
  b: number,
): { h: number; s: number; l: number } {
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r:
        h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
        break;
      case g:
        h = ((b - r) / d + 2) / 6;
        break;
      case b:
        h = ((r - g) / d + 4) / 6;
        break;
    }
  }
  return { h: h * 360, s: s * 100, l: l * 100 };
}

function hslToRgb(
  h: number,
  s: number,
  l: number,
): { r: number; g: number; b: number } {
  const H = h / 360;
  const S = s / 100;
  const L = l / 100;
  let r: number;
  let g: number;
  let b: number;
  if (S === 0) {
    r = g = b = L;
  } else {
    const hue2rgb = (p: number, q: number, t: number) => {
      if (t < 0) t += 1;
      if (t > 1) t -= 1;
      if (t < 1 / 6) return p + (q - p) * 6 * t;
      if (t < 1 / 2) return q;
      if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
      return p;
    };
    const q = L < 0.5 ? L * (1 + S) : L + S - L * S;
    const p = 2 * L - q;
    r = hue2rgb(p, q, H + 1 / 3);
    g = hue2rgb(p, q, H);
    b = hue2rgb(p, q, H - 1 / 3);
  }
  return {
    r: Math.round(r * 255),
    g: Math.round(g * 255),
    b: Math.round(b * 255),
  };
}

export function getChartColorPalette(
  baseColor: string,
  alpha?: number,
): string[] {
  const { r, g, b, a } = parseRgba(baseColor);
  const { h, s } = rgbToHsl(r, g, b);
  const opacity = alpha ?? a;
  // 5 niveles de luminosidad: más oscuro -> más claro (misma tonalidad)
  const lightnessSteps = [25, 40, 55, 70, 85];
  return lightnessSteps.map((lStep) => {
    const { r: R, g: G, b: B } = hslToRgb(h, s, lStep);
    return `rgba(${R}, ${G}, ${B}, ${opacity})`;
  });
}

export const CHART_COLOR_PALETTES: Record<keyof typeof CHART_COLORS, string[]> =
  (Object.keys(CHART_COLORS) as (keyof typeof CHART_COLORS)[]).reduce(
    (acc, key) => {
      acc[key] = getChartColorPalette(CHART_COLORS[key]);
      return acc;
    },
    {} as Record<keyof typeof CHART_COLORS, string[]>,
  );
